import { ProductSelectionState, OfferingLoadState, SchoolOption, SchoolSelectionGroup, CentreSelectionGroup } from "app/pages/DiscountEditor/modules/ProductSelection/state";
import { School, Product, ProductType, AccommodationType } from "app/shared/model";
import { ProductFilterType, SelectionMap, productFilterToDto, ProductFilter, getProductType, getSelectedProductCodes } from "app/shared/model/ProductFilter";
import { SchoolFilterDto, ProductFilterDto, UnitsFilterDto } from "app/shared/model/DiscountDto";
import { UnitsFilter, UnitsFilterType, unitsFilterToDto } from "app/shared/model/ProductUnitFilter";
import { getSelectedDivisionIds } from "app/pages/DiscountEditor/modules/Divisions/selectors";
import { getBookingDateRange, getProductDateRange } from "app/pages/DiscountEditor/modules/DiscountDates/selectors";

function getRoot(state: any) : ProductSelectionState {
    return state.productSelection;
}

export function getProductFilter(state: any) : ProductFilter {
    return getRoot(state).productFilter;
}

export function getSelectedCurrencies(state: any) {
    const root = getRoot(state); 
    const selectedSchoolCodes = Object.keys(root.schoolFilter);
    const selectedSchools = Object.keys(root.allSchools).map(k => root.allSchools[k])
        .filter((x) => selectedSchoolCodes.some((y) => x.code === y)); 

    return root.allCurrencies.filter(
        currencyCode => 
            selectedSchools.some(school => 
                currencyCode.code === school.currencyCode
        )); 
}

export function getSchoolFilterOptions(state: any) : SchoolOption[] {
    const root = getRoot(state);
    return Object.keys(root.allSchools).map(k => root.allSchools[k]);
}

export function getSchoolGroups(state: any) : SchoolSelectionGroup[] {
    const root = getRoot(state)
    const groups = Object.keys(root.schoolGroups).map(k => root.schoolGroups[k]);
    groups.sort((a, b) => a.title.localeCompare(b.title));
    return groups;
}

export function getCentresMap(state: any) : { [code: string]: import("app/shared/model").Centre } {
    return getRoot(state).centresMap;
}

export function getCentreGroupOpenState(state: any) : { [centreCode: string]: boolean } {
    return getRoot(state).centreGroupOpenState;
}

export function getCentreGroupsByCountry(state: any) : { [countryCode: string]: CentreSelectionGroup[] } {
    const root = getRoot(state);
    const schools = Object.keys(root.allSchools).map(k => root.allSchools[k]);
    const centresMap = root.centresMap;
    const schoolFilter = root.schoolFilter;

    const centresByCountry: { [countryCode: string]: { [centreCode: string]: CentreSelectionGroup } } = {};

    schools.forEach(school => {
        const countryCode = school.countryCode;
        const centreCode = school.school.centreCode;
        const centre = centresMap[centreCode];

        if (!centresByCountry[countryCode]) {
            centresByCountry[countryCode] = {};
        }
        if (!centresByCountry[countryCode][centreCode]) {
            centresByCountry[countryCode][centreCode] = {
                centreCode,
                title: centre?.name ?? centreCode,
                isOpen: root.centreGroupOpenState[centreCode] ?? false,
                itemsSelected: 0,
                itemsNotSelected: 0,
                countryCode
            };
        }
        const group = centresByCountry[countryCode][centreCode];
        if (schoolFilter[school.code]) {
            group.itemsSelected++;
        } else {
            group.itemsNotSelected++;
        }
    });

    const result: { [countryCode: string]: CentreSelectionGroup[] } = {};
    Object.keys(centresByCountry).forEach(countryCode => {
        result[countryCode] = Object.values(centresByCountry[countryCode])
            .sort((a, b) => a.title.localeCompare(b.title));
    });
    return result;
}

export function getSelectedSchools(state: any) : SelectionMap {
    return getRoot(state).schoolFilter;
}

export function isDiscountDateRangeComplete(range: { from: string; to: string; neverExpires: boolean } | undefined): boolean {
    if (!range?.from?.trim()) return false;
    if (range.neverExpires) return true;
    return !!range.to?.trim();
}

export function canEditApplicableProducts(state: any): boolean {
    const divisionIds = getSelectedDivisionIds(state);
    if (!divisionIds.some((id) => id > 0)) return false;

    const selectedSchools = getSelectedSchools(state);
    if (Object.keys(selectedSchools).length === 0) return false;

    return isDiscountDateRangeComplete(getBookingDateRange(state))
        && isDiscountDateRangeComplete(getProductDateRange(state));
}

export function getUnitFilter(state: any) : UnitsFilter {
    return getRoot(state).unitsFilter;
}

export enum ProductsListState {
    NoSchools,
    Loading,
    Error,
    Show
}

export function getOfferingSchoolCodes(state: any): string[] {
    const root = state?.productSelection || state?.discountEditor?.productSelection;
    if (!root?.offeringsBySchool) return [];
    return Object.keys(root.offeringsBySchool);
}

export function getSchoolsToLoad(state: any, includeErrors?: boolean) {
    const root = getRoot(state);
    return Object.keys(root.offeringsBySchool).filter(k => {
        const status = root.offeringsBySchool[k];
        if (!status)
            return true;
        return status.loadState == OfferingLoadState.None || 
            (includeErrors && status.loadState == OfferingLoadState.Errored)
    })
}

function hasSavedSpecificProductCodes(productFilter?: ProductFilter): boolean {
    if (!productFilter) return false;
    return Object.keys(getSelectedProductCodes(productFilter)).some(Boolean);
}

export function getProductListState(state: any, productFilter?: ProductFilter) {
    const root = getRoot(state);
    const savedSpecificCodes = hasSavedSpecificProductCodes(productFilter);

    const selectedSchools = Object.keys(root.schoolFilter);
    if (selectedSchools.length == 0 && !savedSpecificCodes)
        return ProductsListState.NoSchools;

    if (root.catalogLoadState === OfferingLoadState.Loading && !savedSpecificCodes)
        return ProductsListState.Loading;

    if (root.catalogLoadState === OfferingLoadState.Errored && !savedSpecificCodes)
        return ProductsListState.Error;

    if (productFilter) {
        const productType = getProductType(productFilter);
        if (productFilter.type === ProductFilterType.ByType && productType !== ProductType.Accommodation) {
            return ProductsListState.Show;
        }
        
        if (productFilter.type === ProductFilterType.IncludeSpecific || 
            productFilter.type === ProductFilterType.ExcludeSpecific) {
            return ProductsListState.Show;
        }
        
        if (productFilter.type === ProductFilterType.Homestay || 
            productFilter.type === ProductFilterType.Residence ||
            (productFilter.type === ProductFilterType.ByType && productType === ProductType.Accommodation)) {
            return ProductsListState.Show;
        }
    }
    return ProductsListState.Show;
}

export function productCodeAliases(code: string): string[] {
    const aliases = [code];
    const match = code.match(/^(Course|AccommodationRoom|Accommodation|AddOn|Transfer|Package)-(-?[0-9]+)$/i);
    if (!match) return aliases;

    const typeName = match[1];
    const numericId = parseInt(match[2], 10);
    if (!Number.isFinite(numericId)) return aliases;

    const publishedId = numericId & 0x7FFFFFFF;
    const unpublishedId = (publishedId | 0x80000000) | 0;
    const typeVariants = /^Accommodation/i.test(typeName)
        ? ["Accommodation", "AccommodationRoom", typeName]
        : [typeName];

    for (const type of typeVariants) {
        aliases.push(`${type}-${publishedId}`);
        aliases.push(`${type}-${unpublishedId}`);
    }

    return [...new Set(aliases)];
}

function lookupProductByCode(allProducts: { [code: string]: Product }, code: string): Product | undefined {
    for (const alias of productCodeAliases(code)) {
        if (allProducts[alias]) return allProducts[alias];
    }
    return undefined;
}

export function getProductByCode(state: any, code: string) : Product|undefined {
    return lookupProductByCode(getRoot(state).allProducts, code);
}

export function getProductMap(state: any) {
    return getRoot(state).allProducts;
}

export function getAllProductsBasedOnCurrentSchool(state: any, productFilter: ProductFilter|undefined) : Product[] {
    if (productFilter === undefined)
        return [];

    const root = getRoot(state);
    const productType = getProductType(productFilter);
    if (productType === undefined)
        return [];

    if (productFilter.type === ProductFilterType.Homestay || 
        productFilter.type === ProductFilterType.Residence ||
        (productFilter.type === ProductFilterType.ByType && productType === ProductType.Accommodation)) {
        return [];
    }

    const requiredProducts = root.productsInCurrentSchools;

    const shouldUseAllProducts = 
        (productFilter.type === ProductFilterType.ByType) ||
        (productFilter.type === ProductFilterType.IncludeSpecific) ||
        (productFilter.type === ProductFilterType.ExcludeSpecific);

    if (shouldUseAllProducts) {
        const output: Product[] = [];
        const codesAdded = new Set<string>(requiredProducts || []);

        if (productFilter.type === ProductFilterType.IncludeSpecific || productFilter.type === ProductFilterType.ExcludeSpecific) {
            Object.keys(getSelectedProductCodes(productFilter)).forEach(code => { if (code) codesAdded.add(code); });
        }
        if (root.allProducts && codesAdded.size > 0) {
            codesAdded.forEach(code => {
                const product = lookupProductByCode(root.allProducts, code);
                if (product && product.type === productType) {
                    output.push({ ...product, code });
                }
            });
        }

        return output;
    }
    
    if (requiredProducts) {
        Object.keys(getSelectedProductCodes(productFilter))
            .forEach(code => { if (code) requiredProducts.add(code) });
    }

    const output : Product[] = [];
    if (requiredProducts) {
        requiredProducts.forEach(code => {
            const product = root.allProducts && lookupProductByCode(root.allProducts, code);
            if (product && (productType === undefined || product.type === productType)) {
                output.push({ ...product, code });
            }
        });
    }
    return output;
}

function offeringHasProduct(productCodes: string[], productCode: string): boolean {
    const aliases = new Set(productCodeAliases(productCode));
    return productCodes.some((code) =>
        aliases.has(code) || productCodeAliases(code).some((alias) => aliases.has(alias))
    );
}

function formatMissingSchoolHint(schoolNames: string[]): string {
    if (schoolNames.length === 1) return `Not offered at ${schoolNames[0]}`;
    if (schoolNames.length === 2) return `Not offered at ${schoolNames[0]} and ${schoolNames[1]}`;
    return `Not offered at ${schoolNames.slice(0, -1).join(', ')}, and ${schoolNames[schoolNames.length - 1]}`;
}

export function getSelectedProductMissingSchoolHints(
    state: any,
    productFilter?: ProductFilter
): { [productCode: string]: string } {
    if (!productFilter) return {};
    if (
        productFilter.type !== ProductFilterType.IncludeSpecific &&
        productFilter.type !== ProductFilterType.ExcludeSpecific
    ) {
        return {};
    }

    const root = getRoot(state);
    const selectedSchoolCodes = Object.keys(root.schoolFilter).filter((code) => root.schoolFilter[code]);
    if (selectedSchoolCodes.length === 0) return {};

    const selectedProductCodes = Object.keys(getSelectedProductCodes(productFilter)).filter(Boolean);
    if (selectedProductCodes.length === 0) return {};

    const hints: { [productCode: string]: string } = {};
    selectedProductCodes.forEach((productCode) => {
        const missingNames: string[] = [];
        selectedSchoolCodes.forEach((schoolCode) => {
            const details = root.offeringsBySchool[schoolCode]
                || root.offeringsBySchool[schoolCode.replace(/^School-/i, '')];
            if (!details || details.loadState !== OfferingLoadState.Loaded) return;
            if (offeringHasProduct(details.productCodes, productCode)) return;
            missingNames.push(root.allSchools[schoolCode]?.name || schoolCode);
        });
        if (missingNames.length > 0) {
            hints[productCode] = formatMissingSchoolHint(missingNames);
        }
    });
    return hints;
}

export function getProductDtos(state: any) : [SchoolFilterDto, ProductFilterDto, UnitsFilterDto] {
    const root = getRoot(state);
    return [
        getSchoolFilterDto(root),
        productFilterToDto(root.productFilter),
        unitsFilterToDto(root.unitsFilter)
    ]
}

function extractSchoolId(schoolCode: string, schoolOption?: SchoolOption): string {
    const cleaned = schoolCode.replace(/^School-/i, '').trim();
    const match = cleaned.match(/\d+$/);
    if (match) {
        return match[0];
    }
    
    const anyNumber = cleaned.match(/\d+/);
    if (anyNumber) {
        return anyNumber[0];
    }
    
    return cleaned;
}

function getSchoolFilterDto(state: ProductSelectionState) : SchoolFilterDto {
    const schoolCodes = Object.keys(state.schoolFilter).filter(x => state.schoolFilter[x]);
    const schoolIds = schoolCodes.map(code => {
    const schoolOption = state.allSchools[code];
        return extractSchoolId(code, schoolOption);
    });
    
    return {
        schoolCodes: schoolIds
    };
}
